import {
  type TimeDisplayMode,
  formatTimeByMode,
} from "@/shared/components/visualization/WorkspaceCtx";
import { CanonicalDataType } from "@/shared/domain/topics";

import type { FilterStage } from "../columnFilters/state";

export interface StageLabelOptions {
  dataType?: CanonicalDataType;
  timeDisplayMode?: TimeDisplayMode;
  earliestTime?: bigint | null;
}

/**
 * Formats a numeric filter value for display.
 *
 * When the column is a timestamp and a time display mode is provided,
 * the raw nanosecond epoch string is formatted as a human-readable timestamp.
 * Falls back to the raw string if parsing fails or no formatting context is given.
 */
function formatNumericValue(raw: string, options?: StageLabelOptions): string {
  if (
    options?.dataType === CanonicalDataType.Timestamp &&
    options.timeDisplayMode !== undefined
  ) {
    try {
      return formatTimeByMode(
        BigInt(raw),
        options.timeDisplayMode,
        options.earliestTime ?? null,
      );
    } catch {
      return raw;
    }
  }
  return raw;
}

export function stageLabel(
  stage: FilterStage,
  options?: StageLabelOptions,
): string {
  switch (stage.condition) {
    case "contains":
      return `contains "${stage.value}"`;
    case "does_not_contain":
      return `not "${stage.value}"`;
    case "is_empty":
      return "is empty";
    case "is_not_empty":
      return "is not empty";
    case "starts_with":
      return `starts with "${stage.value}"`;
    case "ends_with":
      return `ends with "${stage.value}"`;
    case "is_exactly":
      return `= "${stage.value}"`;
    case "matches_regex":
      return `RE(${stage.value})`;
    case "greater_than":
      return `> ${formatNumericValue(stage.value, options)}`;
    case "less_than":
      return `< ${formatNumericValue(stage.value, options)}`;
    case "greater_than_or_equal":
      return `>= ${formatNumericValue(stage.value, options)}`;
    case "less_than_or_equal":
      return `<= ${formatNumericValue(stage.value, options)}`;
    case "between":
      return `${formatNumericValue(stage.value, options)} – ${formatNumericValue(stage.value2 ?? "", options)}`;
    default: {
      const _exhaustive: never = stage.condition;
      return _exhaustive;
    }
  }
}
